import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "../api/client";
import { floatingSheet, theme } from "../theme";
import { fmtDate } from "../utils/format";
import { usePopoverPlacement } from "./usePopoverPlacement";

// ── Auditor notification bell ─────────────────────────────────────────────
// What clients did that the auditor has to look at (GET /auditor/notifications): built from
// findings, evidence requests, evidence and clause approvals on the standards in the reader's own
// scope. Rendered in the shared top bar when topBar.bell is "auditor".

interface AuditorNotification {
  id: string;
  orgId: string;
  orgName: string | null;
  kind: string;
  action: string;
  detail: string | null;
  createdAt: string;
  link: string;
}

const READ_KEY = "auditor_notif_read";

function readLastRead(): string {
  try { return localStorage.getItem(READ_KEY) ?? ""; } catch { return ""; }
}

export function AuditorBell() {
  const [notifications, setNotifications] = useState<AuditorNotification[]>([]);
  const [open, setOpen] = useState(false);
  const [lastRead, setLastRead] = useState<string>(readLastRead);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const unread = notifications.filter((n) => n.createdAt > lastRead).length;
  const placement = usePopoverPlacement(open, buttonRef);

  useEffect(() => {
    const load = () => {
      api.get<{ notifications: AuditorNotification[] }>("/auditor/notifications")
        .then((d) => setNotifications(d.notifications))
        .catch(() => {});
    };
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") { setOpen(false); buttonRef.current?.focus(); }
    }
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = () => {
    const now = new Date().toISOString();
    try { localStorage.setItem(READ_KEY, now); } catch { /* best effort */ }
    setLastRead(now);
    setOpen((v) => !v);
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <button
        ref={buttonRef}
        onClick={toggle}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        aria-haspopup="true"
        aria-expanded={open}
        title="Notifications"
        style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 6px", borderRadius: 3, color: theme.color.muted, position: "relative", display: "flex", alignItems: "center" }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--c-panelAlt)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
      >
        <Bell size={18} />
        {unread > 0 && (
          <span style={{ position: "absolute", top: 0, right: 0, fontSize: 9, fontWeight: 800, background: theme.color.danger, color: "#fff", borderRadius: 3, padding: "0 4px", minWidth: 14, textAlign: "center", lineHeight: "14px" }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div style={{
          ...floatingSheet,
          maxHeight: 400, overflowY: "auto",
          zIndex: 2000, fontFamily: theme.font,
          ...placement,
        }}>
          <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontSize: 12, fontWeight: 700, color: theme.color.heading }}>
            Recent activity
          </div>
          {notifications.length === 0 ? (
            <div style={{ padding: "16px 14px", fontSize: 12, color: theme.color.subtle }}>No activity in the last 30 days.</div>
          ) : (
            notifications.map((n) => (
              <Link key={n.id} to={n.link} onClick={() => setOpen(false)} style={{ padding: "8px 14px", borderBottom: `1px solid ${theme.color.border}`, display: "flex", flexDirection: "column", gap: 2, textDecoration: "none" }}>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {n.orgName && <span style={{ fontSize: 11, fontWeight: 600, color: theme.color.accentText }}>{n.orgName}</span>}
                </div>
                <div style={{ fontSize: 12, color: theme.color.text }}>{n.action}</div>
                {n.detail && <div style={{ fontSize: 11, color: theme.color.subtle, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{n.detail}</div>}
                <div style={{ fontSize: 10, color: theme.color.faint }}>{fmtDate(n.createdAt)}</div>
              </Link>
            ))
          )}
        </div>
      )}
    </div>
  );
}
